/**
 * [SDKR-R3 · #369] service attachment reservation contracts (freeze §6 — ค่าตายตัว)
 * POST /api/v1/service/attachments/reservations · GET (lookup ผ่าน header) · GET /api/v1/service/attachments/:id
 *
 * - pending payload = ฟิลด์ presign เดิม + metadata ที่จำเป็นต่อ resume (content_sha256/reserved window)
 * - ready payload = receipt ที่ไม่มี upload capability (ห้าม write R2 ตอน replay)
 * - state (GET /:id) = metadata ของแถวตัวเอง ไม่มี R2 key/เนื้อไฟล์/signed download URL
 */
import { z } from "zod";
import { ServiceAttachmentPresign, ServiceAttachmentReady } from "./webhooks.ts";

/** body ของ POST /reservations — ฟิลด์ legacy presign + content_sha256 บังคับ (lowercase hex64) */
export const ServiceAttachmentReservationCreate = ServiceAttachmentPresign.extend({
  content_sha256: z.string().regex(/^[0-9a-f]{64}$/, "content_sha256 ต้องเป็น lowercase hex 64 ตัว"),
});
export type ServiceAttachmentReservationCreate = z.infer<typeof ServiceAttachmentReservationCreate>;

/** payload ตอน reservation ยัง pending (สร้างใหม่ 201 / replay 200) — upload_url เป็น capability สั้น 10 นาที */
export const ServiceAttachmentReservationPending = z.object({
  attachment_id: z.uuid(),
  status: z.literal("pending"),
  upload_url: z.string(),
  upload_headers: z.record(z.string(), z.string()),
  /** อายุของ upload_url (10 นาที — สดใหม่ทุกครั้งที่ replay) */
  expires_at: z.iso.datetime(),
  // metadata ที่จำเป็นต่อ resume (fingerprint ของ command)
  filename: z.string(),
  size: z.number().int(),
  mime: z.string().nullable(),
  ticket_id: z.string().nullable(),
  content_sha256: z.string(),
  /** เวลาที่ command ถูกจองครั้งแรก — replay ห้ามเปลี่ยน */
  created_at: z.iso.datetime(),
  /** ปลายหน้าต่าง reservation ของ command — replay ห้าม extend */
  reserved_until: z.iso.datetime(),
});
export type ServiceAttachmentReservationPending = z.infer<typeof ServiceAttachmentReservationPending>;

/** payload ตอน attachment ใต้ reservation เป็น ready — ไม่มี upload capability */
export const ServiceAttachmentReservationReady = z.object({
  attachment_id: z.uuid(),
  status: z.literal("ready"),
  attachment: ServiceAttachmentReady,
});
export type ServiceAttachmentReservationReady = z.infer<typeof ServiceAttachmentReservationReady>;

/** discriminated union ตาม status — ไม่ hand-copy types */
export const ServiceAttachmentReservationReceipt = z.discriminatedUnion("status", [ServiceAttachmentReservationPending, ServiceAttachmentReservationReady]);
export type ServiceAttachmentReservationReceipt = z.infer<typeof ServiceAttachmentReservationReceipt>;

/** GET /api/v1/service/attachments/:id — own state ภายใต้ attachments:write (ไม่ต้อง tickets:read) */
export const ServiceAttachmentState = z.object({
  id: z.uuid(),
  status: z.enum(["pending", "ready"]),
  filename: z.string(),
  size: z.number().int(),
  mime: z.string().nullable(),
  ticket_id: z.string().nullable(),
  created_at: z.iso.datetime(),
  reserved: z.boolean(),
  reserved_until: z.iso.datetime().nullable(),
});
export type ServiceAttachmentState = z.infer<typeof ServiceAttachmentState>;
